/**
 * useInfiniteScrollPhotos Hook
 * 
 * A reusable hook for fetching photos with infinite scroll pagination.
 * Loads photos in batches as the user scrolls.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
   collection,
   query,
   where,
   getDocs,
   getDoc,
   doc,
   limit,
   orderBy,
   startAfter,
   QueryDocumentSnapshot,
   DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase/config';

export interface UseInfiniteScrollPhotosOptions {
   /**
    * Album ID to fetch photos for
    */
   albumId?: string;
   
   /**
    * Number of photos to fetch per batch
    * @default 10
    */
   pageSize?: number;
   
   /**
    * Order by field (optional - if not provided, no ordering is applied)
    */
   orderByField?: string;
   
   /**
    * Order direction
    * @default 'desc'
    */
   orderDirection?: 'asc' | 'desc';
   
   /**
    * Whether to enable infinite scroll
    * @default true
    */
   enabled?: boolean;
}

export interface Photo {
   id: string;
   url: string;
   thumbnailUrl?: string;
   title?: string;
   description?: string;
   fileName?: string;
   size?: number;
   mimeType?: string;
   mediaType?: string;
   videoUrl?: string;
   albumId?: string;
   uploadedBy?: string;
   userId?: string;
   uploadedAt?: any;
   createdAt: any;
   takenAt?: any;
   tags?: string[];
   likeCount?: number;
   likedBy?: string[];
   isEnhanced?: boolean;
   isTransformed?: boolean;
   camera?: {
      make?: string;
      model?: string;
   } | string;
   location?: {
      address?: string;
      name?: string;
      coordinates?: {
         latitude: number;
         longitude: number;
      };
   };
   /** Raw userProfile / userProfiles / users doc; same pattern as POST /api/albums/photos/batch */
   uploaderProfile?: Record<string, unknown>;
}

export const useInfiniteScrollPhotos = (options: UseInfiniteScrollPhotosOptions) => {
   const {
      albumId,
      pageSize = 10,
      orderByField,
      orderDirection = 'desc',
      enabled = true,
   } = options;

   const [photos, setPhotos] = useState<Photo[]>([]);
   const [loading, setLoading] = useState(false);
   const [hasMore, setHasMore] = useState(true);
   const [error, setError] = useState<string | null>(null);
   
   const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
   const isInitialLoadRef = useRef(true);
   const isFetchingRef = useRef(false); // Add ref to prevent race conditions
   const loadedPhotoIdsRef = useRef(new Set<string>()); // Track loaded photo IDs to prevent duplicates
   const hasMoreRef = useRef(true); // Use ref to avoid stale closure

   /**
    * Fetch photos from Firestore
    */
   const fetchPhotos = useCallback(async (isInitial: boolean = false) => {
      if (!enabled || !albumId) {
         console.log('❌ Fetch blocked:', { enabled, albumId });
         return;
      }
      
      // Use ref to prevent race conditions - this is the main guard
      if (isFetchingRef.current) {
         console.log('⏸️ Already fetching, skipping...');
         return;
      }
      
      // Check if we have more photos to load (use ref to avoid stale closure)
      if (!isInitial && !hasMoreRef.current) {
         console.log('❌ No more photos to load');
         return;
      }

      console.log('📸 Fetching photos:', { albumId, isInitial, pageSize, orderByField });
      
      // Set fetching flag immediately
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);

      try {
         // Build query
         let photosQuery;
         
         if (orderByField) {
            // With explicit ordering
            photosQuery = query(
               collection(db, 'photos'),
               where('albumId', '==', albumId),
               orderBy(orderByField, orderDirection),
               limit(pageSize)
            );

            // If not initial load, start after the last document
            if (!isInitial && lastDocRef.current) {
               photosQuery = query(
                  collection(db, 'photos'),
                  where('albumId', '==', albumId),
                  orderBy(orderByField, orderDirection),
                  startAfter(lastDocRef.current),
                  limit(pageSize)
               );
            }
         } else {
            // Without ordering - use document ID for consistent pagination
            photosQuery = query(
               collection(db, 'photos'),
               where('albumId', '==', albumId),
               limit(pageSize)
            );

            // If not initial load, start after the last document
            if (!isInitial && lastDocRef.current) {
               photosQuery = query(
                  collection(db, 'photos'),
                  where('albumId', '==', albumId),
                  startAfter(lastDocRef.current),
                  limit(pageSize)
               );
            }
         }

         const snapshot = await getDocs(photosQuery);

         console.log('📊 Query result:', { 
            empty: snapshot.empty, 
            count: snapshot.docs.length,
            albumId 
         });

         if (snapshot.empty || snapshot.docs.length === 0) {
            console.log('⚠️ No more photos found for album:', albumId);
            hasMoreRef.current = false;
            setHasMore(false);
            setLoading(false);
            isFetchingRef.current = false;
            return;
         }

         // Store the last document for pagination
         lastDocRef.current = snapshot.docs[snapshot.docs.length - 1];

         // Map snapshot to Photo objects and filter out duplicates
         const newPhotos: Photo[] = snapshot.docs
            .map((docSnap) => {
               const data = docSnap.data();
               const mergedUploaderId = String(data.userId || data.uploadedBy || '').trim();
               return {
                  id: docSnap.id,
                  url: data.url || '',
                  thumbnailUrl: data.thumbnailUrl || data.url || '',
                  title: data.title || '',
                  description: data.description || '',
                  fileName: data.fileName || '',
                  size: data.size || 0,
                  mimeType: data.mimeType || '',
                  mediaType: data.mediaType || 'photo',
                  videoUrl: data.videoUrl || data.url || '',
                  albumId: data.albumId || '',
                  uploadedBy: mergedUploaderId || data.uploadedBy || '',
                  userId: mergedUploaderId || data.userId || '',
                  uploadedAt: data.uploadedAt,
                  createdAt: data.createdAt,
                  takenAt: data.takenAt,
                  tags: data.tags || [],
                  likeCount: data.likeCount || 0,
                  likedBy: data.likedBy || [],
                  isEnhanced: data.isEnhanced || false,
                  isTransformed: data.isTransformed || false,
                  location: data.location || undefined,
               };
            })
            .filter((photo) => {
               // Filter out duplicates
               if (loadedPhotoIdsRef.current.has(photo.id)) {
                  console.log('🔄 Skipping duplicate photo:', photo.id);
                  return false;
               }
               return true;
            });

         const uploaderIds = Array.from(
            new Set(
               newPhotos
                  .map((p) => String(p.userId || p.uploadedBy || '').trim())
                  .filter((id): id is string => Boolean(id))
            )
         );
         const profilesByUid: Record<string, Record<string, unknown>> = {};
         for (const uid of uploaderIds) {
            try {
               let profileDoc = await getDoc(doc(db, 'userProfile', uid));
               if (!profileDoc.exists()) profileDoc = await getDoc(doc(db, 'userProfiles', uid));
               if (!profileDoc.exists()) profileDoc = await getDoc(doc(db, 'users', uid));
               if (profileDoc.exists() && profileDoc.data()) {
                  profilesByUid[uid] = profileDoc.data() as Record<string, unknown>;
               }
            } catch {
               // ignore per-uploader failures
            }
         }
         newPhotos.forEach((photo) => {
            const uid = String(photo.userId || photo.uploadedBy || '').trim();
            if (uid && profilesByUid[uid]) {
               photo.uploaderProfile = profilesByUid[uid];
            }
         });

         // Add new photo IDs to the set
         newPhotos.forEach((photo) => loadedPhotoIdsRef.current.add(photo.id));

         // Check if there are more photos to load
         const morePhotosExist = snapshot.docs.length === pageSize;
         hasMoreRef.current = morePhotosExist;
         setHasMore(morePhotosExist);

         // Append or replace photos
         if (isInitial) {
            console.log('✅ Setting initial photos:', newPhotos.length);
            // Reset the loaded IDs set for initial load
            loadedPhotoIdsRef.current.clear();
            newPhotos.forEach((photo) => loadedPhotoIdsRef.current.add(photo.id));
            setPhotos(newPhotos);
         } else {
            console.log('✅ Appending photos:', newPhotos.length);
            setPhotos((prev) => [...prev, ...newPhotos]);
         }
      } catch (err) {
         console.error('❌ Error fetching photos:', err);
         setError(err instanceof Error ? err.message : 'Failed to fetch photos');
         hasMoreRef.current = false;
         setHasMore(false);
      } finally {
         setLoading(false);
         isFetchingRef.current = false; // Reset fetching flag
      }
   }, [albumId, enabled, pageSize, orderByField, orderDirection]); // Removed loading and hasMore to prevent circular dependency

   /**
    * Load more photos (for manual trigger or infinite scroll)
    */
   const loadMore = useCallback(() => {
      // Don't trigger if already fetching or no more photos (use ref to avoid stale closure)
      if (!isFetchingRef.current && hasMoreRef.current) {
         fetchPhotos(false);
      }
   }, [fetchPhotos]);

   /**
    * Reset and reload photos from the beginning
    */
   const reload = useCallback(() => {
      lastDocRef.current = null;
      isFetchingRef.current = false;
      loadedPhotoIdsRef.current.clear();
      hasMoreRef.current = true;
      setPhotos([]);
      setHasMore(true);
      setError(null);
      isInitialLoadRef.current = true;
      fetchPhotos(true);
   }, [fetchPhotos]);

   /**
    * Initial load
    */
   useEffect(() => {
      console.log('🔄 Initial load effect:', { 
         enabled, 
         albumId, 
         isInitial: isInitialLoadRef.current,
         willTrigger: enabled && albumId && isInitialLoadRef.current 
      });
      if (enabled && albumId && isInitialLoadRef.current) {
         console.log('🚀 Triggering initial fetch for album:', albumId);
         isInitialLoadRef.current = false;
         fetchPhotos(true);
      } else {
         console.log('⏸️ Not triggering initial fetch:', {
            enabled,
            albumId,
            isInitial: isInitialLoadRef.current
         });
      }
   }, [albumId, enabled, fetchPhotos]);

   /**
    * Reset when albumId changes
    */
   useEffect(() => {
      lastDocRef.current = null;
      isFetchingRef.current = false;
      loadedPhotoIdsRef.current.clear();
      hasMoreRef.current = true;
      setPhotos([]);
      setHasMore(true);
      setError(null);
      isInitialLoadRef.current = true;
   }, [albumId]);

   return {
      photos,
      loading,
      hasMore,
      error,
      loadMore,
      reload,
   };
};

/**
 * Hook to detect when user scrolls near bottom of element
 * 
 * @param callback - Function to call when near bottom
 * @param options - Configuration options
 */
export const useInfiniteScroll = (
   callback: () => void,
   options: { 
      threshold?: number; 
      enabled?: boolean;
      containerRef?: React.RefObject<HTMLElement>;
   } = {}
) => {
   const { threshold = 200, enabled = true, containerRef } = options;
   const lastCallTimeRef = useRef<number>(0);
   const throttleDelay = 300; // 300ms throttle to prevent rapid firing

   useEffect(() => {
      if (!enabled) return;

      const handleScroll = () => {
         // Throttle scroll events
         const now = Date.now();
         if (now - lastCallTimeRef.current < throttleDelay) {
            return;
         }

         // If containerRef is provided, use container scroll
         if (containerRef?.current) {
            const container = containerRef.current;
            const scrollPosition = container.scrollTop + container.clientHeight;
            const bottomPosition = container.scrollHeight;
            const distanceFromBottom = bottomPosition - scrollPosition;

            if (distanceFromBottom < threshold) {
               console.log('📜 Container scroll triggered load more');
               lastCallTimeRef.current = now;
               callback();
            }
         } else {
            // Fallback to window scroll
            const scrollPosition = window.innerHeight + window.scrollY;
            const bottomPosition = document.documentElement.scrollHeight;
            const distanceFromBottom = bottomPosition - scrollPosition;

            if (distanceFromBottom < threshold) {
               console.log('📜 Window scroll triggered load more');
               lastCallTimeRef.current = now;
               callback();
            }
         }
      };

      // Attach scroll listener to container or window
      const scrollTarget = containerRef?.current || window;
      scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
         scrollTarget.removeEventListener('scroll', handleScroll);
      };
   }, [callback, threshold, enabled, containerRef]);
};

